import {
  Columns2,
  Download,
  FileImage,
  GalleryHorizontal,
  MousePointerClick,
  PanelRight,
  Sheet,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";

import {
  API_PREFIX,
  ApiError,
  listRunInstances,
  mergeUnit,
  patchUnit,
  reviewInstance,
  reviewInstances,
  takeoffCsvUrl,
  takeoffPdfUrl,
} from "@/api/client";
import type { Instance, ReviewCreate } from "@/api/client";
import { useCan } from "@/auth/AuthProvider";
import { baseName, saveFile } from "@/flow/download";
import { StepHeader } from "@/flow/StepHeader";
import { Legend } from "@/flow/Legend";
import { panelsInBox } from "@/flow/block";
import { PanelCard } from "@/flow/PanelCard";
import { RenameDialog } from "@/flow/RenameDialog";
import { sheetName } from "@/flow/sheets";
import { paintFor } from "@/flow/takeoff";
import { useDocument } from "@/hooks/useDocument";
import { useRun } from "@/hooks/useRun";
import { useTakeoff } from "@/hooks/useTakeoff";
import { unitOf } from "@/viewer/PanelOverlay";
import { Button } from "@/ui/Button";
import { cn } from "@/ui/cn";
import { Notice } from "@/ui/Notice";
import { Kbd, Skeleton, Spinner } from "@/ui/primitives";
import type { BBoxNorm } from "@/viewer/bbox";
import { SplitView } from "@/viewer/SplitView";
import type { Layout } from "@/viewer/SplitView";

const LAYOUTS: readonly { key: Layout; label: string; icon: typeof Columns2 }[] = [
  { key: "split", label: "Side by side", icon: Columns2 },
  { key: "takeoff", label: "Take-off only", icon: PanelRight },
  { key: "original", label: "Original only", icon: FileImage },
];

const message = (err: unknown, fallback: string) =>
  err instanceof ApiError ? err.detail : fallback;

type Saving = { kind: "idle" } | { kind: "busy"; what: string } | { kind: "error"; msg: string };

/**
 * Step four, and the screen the whole flow exists for: the architect's sheet beside the
 * same sheet marked up, moving together, with the key to the colours down the side.
 */
export function TakeoffScreen() {
  const { id = "", runId = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const { doc } = useDocument(id);
  const { run: runInfo } = useRun(runId);
  const { takeoff, loaded, error, reload } = useTakeoff(runId);
  const canReview = useCan("review:write");
  const [instances, setInstances] = useState<Instance[]>([]);
  const [instancesError, setInstancesError] = useState<string | null>(null);
  const [loadingSheet, setLoadingSheet] = useState(false);
  const [layout, setLayout] = useState<Layout>("split");
  const [activeUnit, setActiveUnit] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [decisionError, setDecisionError] = useState<string | null>(null);
  /** The configuration being renamed, the panel it was opened from, and a boxed block. */
  const [renaming, setRenaming] = useState<{
    unitId: string;
    instanceId: string | null;
    block?: string[];
  } | null>(null);
  /** While set, a drag on the take-off boxes a block of this panel's configuration (K4). */
  const [boxing, setBoxing] = useState<{ unitId: string; anchorId: string } | null>(null);
  const [saving, setSaving] = useState<Saving>({ kind: "idle" });

  const sheets = useMemo(() => takeoff?.sheets ?? [], [takeoff]);
  const asked = params.get("sheet");
  const first = sheets.find((s) => s.panel_count > 0) ?? sheets[0];
  const pageIndex =
    asked !== null && sheets.some((s) => String(s.page_index) === asked)
      ? Number(asked)
      : (first?.page_index ?? null);
  const sheet = sheets.find((s) => s.page_index === pageIndex) ?? null;

  const loadInstances = useCallback(async () => {
    if (pageIndex === null) {
      setInstances([]);
      return;
    }
    try {
      const body = await listRunInstances(runId, pageIndex);
      setInstances(body.instances);
      setInstancesError(null);
    } catch (err) {
      setInstancesError(message(err, "could not load the panels on this sheet"));
    }
  }, [runId, pageIndex]);

  useEffect(() => {
    let cancelled = false;
    setLoadingSheet(true);
    setSelectedId(null);
    setInstances([]);
    void loadInstances().finally(() => {
      if (!cancelled) {
        setLoadingSheet(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [loadInstances]);

  const configurations = useMemo(() => takeoff?.configurations ?? [], [takeoff]);
  const paint = useMemo(() => paintFor(configurations), [configurations]);
  const onSheet = useMemo(() => new Set(instances.map(unitOf)), [instances]);
  const selected = instances.find((i) => i.id === selectedId) ?? null;

  const refresh = useCallback(async () => {
    await Promise.all([reload(), loadInstances()]);
  }, [reload, loadInstances]);

  const name = useCallback(
    async (unitId: string, mark: string) => {
      try {
        await patchUnit(unitId, { mark });
        setActionError(null);
        await refresh();
        return true;
      } catch (err) {
        setActionError(message(err, "could not save the name"));
        return false;
      }
    },
    [refresh],
  );

  const merge = useCallback(
    async (unitId: string, intoUnitId: string) => {
      try {
        await mergeUnit(unitId, intoUnitId);
        setActionError(null);
        setActiveUnit(intoUnitId);
        await refresh();
        return true;
      } catch (err) {
        setActionError(message(err, "could not merge the configurations"));
        return false;
      }
    },
    [refresh],
  );

  const decide = useCallback(
    async (instanceId: string, body: ReviewCreate) => {
      try {
        await reviewInstance(instanceId, body);
        setDecisionError(null);
        await refresh();
        return true;
      } catch (err) {
        setDecisionError(message(err, "could not record the decision"));
        return false;
      }
    },
    [refresh],
  );

  const decideMany = useCallback(
    async (instanceIds: readonly string[], body: ReviewCreate) => {
      try {
        await reviewInstances(instanceIds, body);
        setDecisionError(null);
        await refresh();
        return true;
      } catch (err) {
        setDecisionError(message(err, "could not record the decisions"));
        return false;
      }
    },
    [refresh],
  );

  const boxed = useCallback(
    (box: BBoxNorm) => {
      if (!boxing) return;
      const ids = panelsInBox(instances, box, boxing.unitId).map((i) => i.id);
      const block = ids.includes(boxing.anchorId) ? ids : [boxing.anchorId, ...ids];
      setBoxing(null);
      setActionError(null);
      setDecisionError(null);
      setRenaming({ unitId: boxing.unitId, instanceId: boxing.anchorId, block });
    },
    [boxing, instances],
  );

  useEffect(() => {
    if (!boxing) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setBoxing(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [boxing]);

  useEffect(() => {
    // Boxing and the rename dialog own Escape while they are open.
    if (!selectedId || boxing || renaming) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented) setSelectedId(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedId, boxing, renaming]);

  const pickedIds = useMemo(() => (renaming?.block ? new Set(renaming.block) : null), [renaming]);

  const save = useCallback(async (url: string, filename: string, what: string) => {
    setSaving({ kind: "busy", what });
    try {
      await saveFile(url, filename);
      setSaving({ kind: "idle" });
    } catch (err) {
      const missing = err instanceof ApiError && (err.status === 404 || err.status === 405);
      setSaving({
        kind: "error",
        msg: missing
          ? `This server cannot build the ${what} yet.`
          : `Could not build the ${what}: ${message(err, "the server did not answer")}`,
      });
    }
  }, []);

  const pick = useCallback((instance: Instance) => {
    setSelectedId((current) => (current === instance.id ? null : instance.id));
  }, []);

  const openRename = (unitId: string, instanceId: string | null) => {
    setActionError(null);
    setDecisionError(null);
    setRenaming({ unitId, instanceId });
  };
  const renamed = renaming
    ? (configurations.find((c) => c.unit_id === renaming.unitId) ?? null)
    : null;

  const goTo = (index: number) => {
    const next = new URLSearchParams(params);
    next.set("sheet", String(index));
    setParams(next, { replace: true });
  };

  const stem = baseName(doc?.filename);
  const totals = takeoff?.totals;

  return (
    <main className="flex h-full flex-col bg-bg">
      <StepHeader
        step="takeoff"
        documentId={id}
        filename={doc?.filename}
        runId={runId}
        runFinished
        review={runInfo?.mode_chosen === "configurations"}
      >
        <Button asChild variant="secondary" size="sm">
          <Link to={`/documents/${id}/runs/${runId}/types${activeUnit ? `/${activeUnit}` : ""}`}>
            <GalleryHorizontal aria-hidden="true" />
            <span className="hidden lg:inline">Review types</span>
          </Link>
        </Button>
        <Button asChild size="sm">
          <a href={takeoffCsvUrl(runId)} download={`${stem}-unit-take-off.csv`}>
            <Sheet aria-hidden="true" />
            <span className="hidden lg:inline">Table (CSV)</span>
          </a>
        </Button>
        <Button
          variant="primary"
          size="sm"
          disabled={saving.kind === "busy" || !takeoff}
          onClick={() =>
            void save(takeoffPdfUrl(runId), `${stem}-unit-take-off.pdf`, "take-off PDF")
          }
        >
          {saving.kind === "busy" && saving.what === "take-off PDF" ? (
            <Spinner />
          ) : (
            <Download aria-hidden="true" />
          )}
          {saving.kind === "busy" && saving.what === "take-off PDF"
            ? "Preparing PDF…"
            : "Download take-off PDF"}
        </Button>
      </StepHeader>

      {saving.kind === "error" || error ? (
        <div className="space-y-2 border-b border-line bg-surface px-4 py-2">
          {saving.kind === "error" ? <Notice tone="danger">{saving.msg}</Notice> : null}
          {error ? <Notice tone="danger">{error}</Notice> : null}
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1">
        <aside className="flex w-72 shrink-0 flex-col overflow-y-auto border-r border-line bg-surface">
          <dl className="grid grid-cols-3 gap-2 p-4" aria-label="Totals">
            <Stat label="Panels" value={totals ? totals.panels.toLocaleString("en-US") : null} />
            <Stat label="Configs" value={totals ? String(totals.configurations) : null} />
            <Stat
              label="Named"
              value={totals ? `${totals.named}/${totals.configurations}` : null}
            />
          </dl>

          <section aria-label="Sheets in the take-off" className="border-t border-line">
            <h2 className="px-4 pt-4 pb-2 text-xs font-semibold tracking-wide text-muted uppercase">
              Sheets
            </h2>
            <ul className="flex flex-col gap-0.5 px-2 pb-3">
              {!loaded
                ? [0, 1, 2].map((i) => (
                    <li key={i} className="px-2 py-1.5">
                      <Skeleton className="h-8" />
                    </li>
                  ))
                : null}
              {sheets.map((s) => {
                const on = s.page_index === pageIndex;
                return (
                  <li key={s.page_index}>
                    <button
                      type="button"
                      aria-current={on ? "page" : undefined}
                      onClick={() => goTo(s.page_index)}
                      className={cn(
                        "flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left",
                        on ? "bg-accent-soft text-ink" : "text-ink-2 hover:bg-sunken",
                      )}
                    >
                      <span
                        className={cn(
                          "w-14 shrink-0 font-mono text-[13px] font-medium",
                          on ? "text-accent" : "text-ink",
                        )}
                      >
                        {sheetName(s)}
                      </span>
                      <span
                        className="min-w-0 flex-1 truncate text-[13px]"
                        title={s.sheet_title ?? ""}
                      >
                        {s.sheet_title ?? ""}
                      </span>
                      {s.panel_count ? (
                        <span className="tabular shrink-0 rounded-full bg-sunken px-1.5 text-[11px] text-muted">
                          {s.panel_count.toLocaleString("en-US")}
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
              {loaded && sheets.length === 0 ? (
                <li className="px-2 py-3 text-[13px] text-muted">
                  No panels were found.{" "}
                  <Link className="text-accent" to={`/documents/${id}/sheets`}>
                    Choose sheets
                  </Link>{" "}
                  that include the exterior elevations.
                </li>
              ) : null}
            </ul>
          </section>

          <div className="border-t border-line">
            <Legend
              configurations={configurations}
              onSheet={onSheet}
              activeId={activeUnit}
              onActivate={setActiveUnit}
              onName={name}
              onRename={(unitId) => openRename(unitId, null)}
              onMerge={merge}
              error={actionError}
              readOnly={!canReview}
            />
          </div>
        </aside>

        <section className="flex min-w-0 flex-1 flex-col" aria-label="Sheet">
          <div className="flex items-center gap-3 border-b border-line bg-surface px-4 py-2">
            <h1 className="flex min-w-0 flex-1 items-baseline gap-2">
              {sheet ? (
                <>
                  <span className="font-mono text-[15px] font-semibold text-ink">
                    {sheetName(sheet)}
                  </span>
                  <span className="truncate text-sm text-ink-2">{sheet.sheet_title}</span>
                </>
              ) : loaded ? (
                <span className="text-sm text-muted">No sheet</span>
              ) : (
                <Skeleton className="h-5 w-48" />
              )}
              {loadingSheet ? (
                <Spinner className="size-3.5 self-center text-muted" label="Loading panels" />
              ) : null}
            </h1>
            <div
              className="flex rounded-lg border border-line bg-sunken p-0.5"
              role="group"
              aria-label="View"
            >
              {LAYOUTS.map((option) => {
                const Icon = option.icon;
                const on = layout === option.key;
                return (
                  <button
                    key={option.key}
                    type="button"
                    aria-pressed={on}
                    title={option.label}
                    onClick={() => setLayout(option.key)}
                    className={cn(
                      "flex h-7 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium text-ink-2 hover:text-ink",
                      on && "bg-surface text-ink shadow-card",
                    )}
                  >
                    <Icon aria-hidden="true" className="size-3.5" />
                    <span className="hidden 2xl:inline">{option.label}</span>
                    <span className="sr-only 2xl:hidden">{option.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
          {boxing ? (
            <div
              className="flex items-center justify-between gap-3 border-b border-line bg-accent-soft px-4 py-2 text-[13px] text-ink"
              role="status"
            >
              <span>
                Drag a box over the block of{" "}
                <strong className="font-mono">
                  {paint.get(boxing.unitId)?.mark ?? "this type"}
                </strong>{" "}
                to relabel those panels together. Other types inside the box are left alone.
              </span>
              <Button size="sm" variant="ghost" onClick={() => setBoxing(null)}>
                Cancel <Kbd>Esc</Kbd>
              </Button>
            </div>
          ) : null}
          {instancesError ? (
            <div className="border-b border-line bg-surface px-4 py-2">
              <Notice tone="danger">{instancesError}</Notice>
            </div>
          ) : null}
          {pageIndex !== null ? (
            <SplitView
              key={pageIndex}
              src={`${API_PREFIX}/documents/${id}/pages/${pageIndex}/l0.png`}
              srcLarge={`${API_PREFIX}/documents/${id}/pages/${pageIndex}/l1.png`}
              name={sheet ? sheetName(sheet) : ""}
              instances={instances}
              paint={paint}
              highlightUnitId={boxing?.unitId ?? activeUnit}
              selectedId={selectedId}
              onPick={pick}
              layout={layout}
              onBox={boxing ? boxed : null}
              pickedIds={pickedIds}
            />
          ) : (
            <div className="flex-1 bg-stage" />
          )}
        </section>

        <aside
          className={cn(
            "w-80 shrink-0 overflow-y-auto border-l border-line bg-surface",
            selected ? "block" : "hidden xl:block",
          )}
          aria-label="Panel details"
        >
          {selected ? (
            <PanelCard
              instance={selected}
              configurations={configurations}
              onDecide={decide}
              onRelabel={() => openRename(unitOf(selected), selected.id)}
              onBoxBlock={() => setBoxing({ unitId: unitOf(selected), anchorId: selected.id })}
              onClose={() => setSelectedId(null)}
              error={decisionError}
              readOnly={!canReview}
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
              <span className="grid size-10 place-items-center rounded-full bg-sunken text-muted">
                <MousePointerClick aria-hidden="true" className="size-5" />
              </span>
              <p className="text-sm font-medium text-ink">No panel selected</p>
              <p className="text-[13px] text-muted">
                Click a panel on the take-off to see its size, floor and bay, and to accept, reject
                or relabel it.
              </p>
            </div>
          )}
        </aside>
      </div>
      {renamed ? (
        <RenameDialog
          open
          onOpenChange={(open) => {
            if (!open) {
              setRenaming(null);
            }
          }}
          configuration={renamed}
          configurations={configurations}
          instanceId={renaming?.instanceId ?? null}
          blockIds={renaming?.block ?? null}
          onRenameAll={name}
          onMerge={merge}
          onDecide={decide}
          onDecideMany={decideMany}
          error={actionError ?? decisionError}
        />
      ) : null}
    </main>
  );
}

function Stat({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="rounded-lg bg-sunken px-2.5 py-2">
      <dt className="text-[10px] font-semibold tracking-wide text-muted uppercase">{label}</dt>
      <dd className="tabular mt-0.5 text-[15px] font-semibold text-ink">
        {value ?? <Skeleton className="mt-1 h-4 w-8" />}
      </dd>
    </div>
  );
}
